import type { SignageCommandDeliveryChannel, SignageCommandLogStatus, SignageCommandType } from '@navio/nsdk';
import { SignageCommandDeliveryChannelLabels } from '@navio/nsdk';
import type { BadgeVariant } from 'noddi-ui-web';
import { signageFormatDurationS } from './signageHeartbeatTiming';

export const COMMAND_LOG_STATUS_LABEL: Record<SignageCommandLogStatus, string> = {
  queued: 'Queued',
  delivered: 'Delivered',
  dropped: 'Dropped',
  cancelled: 'Canceled'
};

export const COMMAND_LOG_STATUS_VARIANT: Record<SignageCommandLogStatus, BadgeVariant> = {
  queued: 'info',
  delivered: 'success',
  dropped: 'destructive',
  cancelled: 'outline'
};

export const COMMAND_LOG_STATUS_DESCRIPTION: Record<SignageCommandLogStatus, string> = {
  queued:
    'Not yet on the device. Waiting for Heimdall to mesh-push, or for the Pi to pull the command from Heimdall on the next OS heartbeat. Heartbeat cannot deliver a command that never reached Heimdall.',
  delivered:
    'Reached the device — either pushed immediately via Heimdall, or pulled when the device reported an OS heartbeat.',
  dropped: 'Removed from the queue without delivery (superseded by a newer command or cleared).',
  cancelled: 'Canceled before delivery (staff cancel on product queue and best-effort on Heimdall).'
};

export const COMMAND_DELIVERY_CHANNEL_LABEL: Record<SignageCommandDeliveryChannel, string> = {
  // Product delivery_channel remains "relay"; Heimdall is the fleet hub that replaced fleet-relay.
  relay: SignageCommandDeliveryChannelLabels.RELAY,
  heartbeat: SignageCommandDeliveryChannelLabels.HEARTBEAT
};

/** Product sends `relay` / `heartbeat`; the rest are pre-Heimdall row values still in the log. */
const DELIVERY_CHANNEL_ALIAS: Record<string, SignageCommandDeliveryChannel> = {
  relay: 'relay',
  fleet_relay: 'relay',
  'fleet-relay': 'relay',
  heimdall: 'relay',
  mesh: 'relay',
  pushed: 'relay',
  heartbeat: 'heartbeat',
  os_heartbeat: 'heartbeat',
  'os-heartbeat': 'heartbeat'
};

/**
 * Resolve delivery path for a command log row.
 * Prefer explicit `deliveryChannel` from the API; fall back to enqueue→deliver
 * latency when older rows predate that field (Heimdall mesh is near-instant).
 */
export const signageCommandDeliveryChannel = (log: {
  deliveryChannel?: string | null;
  createdAt?: string | null;
  deliveredAt?: string | null;
  status?: string | null;
}): SignageCommandDeliveryChannel | null => {
  const explicit = DELIVERY_CHANNEL_ALIAS[(log.deliveryChannel ?? '').trim().toLowerCase()];
  if (explicit) {
    return explicit;
  }

  if (log.status !== 'delivered' || !log.createdAt || !log.deliveredAt) {
    return null;
  }
  const start = Date.parse(log.createdAt);
  const end = Date.parse(log.deliveredAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }
  // Heimdall mesh marks delivered in the same staff request (ms–few s). Heartbeat is typically 30–120s+.
  return end - start < 15_000 ? 'relay' : 'heartbeat';
};

/** Human labels for allowlisted pull-commands — exhaustive so a new backend command fails to compile. */
const SIGNAGE_COMMAND_LABEL: Record<SignageCommandType, string> = {
  restart_browser: 'Restart browser',
  refresh: 'Refresh display',
  reboot: 'Reboot device',
  add_wifi: 'Add WiFi',
  switch_display: 'Switch display app',
  update_app: 'Update app',
  rollback_app: 'Rollback app',
  enable_app: 'Enable app',
  disable_app: 'Disable app',
  install_app: 'Install app',
  os_update: 'OS update',
  reconcile: 'Reconcile',
  tv_wake: 'TV on',
  tv_standby: 'TV off',
  tv_mute: 'TV mute',
  tv_volume: 'TV volume',
  tv_probe: 'Refresh probe',
  identify: 'Identify device',
  capture_screenshot: 'Capture still (ops)'
};

export const signageCommandLabel = (cmd: string): string =>
  SIGNAGE_COMMAND_LABEL[cmd as SignageCommandType] ?? cmd.replace(/_/g, ' ');

/** Human labels for known OTA / display app ids. */
const SIGNAGE_APP_LABEL: Record<string, string> = {
  'keel-api': 'Keel API',
  'keel-heartbeat': 'Keel heartbeat',
  signage: 'Signage',
  labelproxy: 'Labelproxy',
  'labelproxy-docs': 'Labelproxy docs',
  'grafana-kiosk': 'Grafana kiosk'
};

export const signageAppLabel = (appId: string | null | undefined): string | null => {
  const id = appId?.trim();
  if (!id) {
    return null;
  }
  return SIGNAGE_APP_LABEL[id] ?? id;
};

/** Package target shown in command history (app id or Keel/OS). */
export const signageCommandPackageLabel = (args: { cmd: string; app?: string | null }): string | null => {
  if (args.cmd === 'os_update') {
    return 'Keel platform';
  }
  return signageAppLabel(args.app);
};

/** "from → to" version line for OTA audit rows; null when nothing known. */
export const signageCommandVersionChange = (args: {
  fromVersion?: string | null;
  toVersion?: string | null;
}): string | null => {
  const from = args.fromVersion?.trim() || null;
  const to = args.toVersion?.trim() || null;
  if (from && to) {
    return `${from} → ${to}`;
  }
  if (from && !to) {
    return `${from} → latest`;
  }
  if (!from && to) {
    return `unknown → ${to}`;
  }
  return null;
};

export const signageUserLabel = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null
): string | null => {
  if (!user) {
    return null;
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || null;
};

/** Delivery latency from enqueue → delivered (Heimdall or heartbeat). */
export const signageCommandDeliveryDuration = (
  createdAt: string | null | undefined,
  deliveredAt: string | null | undefined
): string | null => {
  if (!createdAt || !deliveredAt) {
    return null;
  }
  const start = Date.parse(createdAt);
  const end = Date.parse(deliveredAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }
  const totalMs = end - start;
  return totalMs < 1000 ? `${totalMs} ms` : signageFormatDurationS(Math.round(totalMs / 1000));
};

/** Waiting time for still-queued commands (from createdAt to now). */
export const signageCommandWaitDuration = (createdAt: string | null | undefined, nowMs = Date.now()): string | null => {
  if (!createdAt) {
    return null;
  }
  const start = Date.parse(createdAt);
  if (!Number.isFinite(start) || nowMs < start) {
    return null;
  }
  return signageCommandDeliveryDuration(createdAt, new Date(nowMs).toISOString());
};
